import { PhysioLayout } from "@/components/layout/physio-layout";
import { GraduationCap, CheckCircle } from "lucide-react";

const ACADEMIC_ROUTES = [
  {
    title: "PhD / Doctoral Research",
    description: "The primary route to an independent academic career. Can be full-time (3 years) or part-time (5–7 years) alongside NHS practice.",
    steps: [
      "Identify a research question aligned with your clinical expertise (rheumatology physiotherapy outcomes, exercise interventions, patient experience)",
      "Find a suitable university supervisor and department (physiotherapy, rheumatology, health sciences)",
      "Apply for NIHR, Medical Research Council, Versus Arthritis, or university funding",
      "Consider integrated clinical academic fellowship programmes (NIHR ICA)",
      "Maintain NHS clinical practice throughout — honorary/bank contract or fractional appointment",
    ],
  },
  {
    title: "NIHR Clinical Academic Fellowships",
    description: "NIHR offers structured clinical academic training at pre-doctoral, doctoral, and post-doctoral levels for Allied Health Professionals.",
    steps: [
      "Pre-doctoral: NIHR ICA Pre-doctoral Fellowship (12-24 months — develop a research proposal and skills)",
      "Doctoral: NIHR ICA Doctoral Fellowship (PhD studentship with clinical salary support)",
      "Post-doctoral: NIHR Advanced Fellowship (research independence and academic career development)",
      "Check eligibility — must be in an NHS post and registered with HCPC",
      "Applications open annually — see nihr.ac.uk/careers for current rounds",
    ],
  },
  {
    title: "Honorary Academic Appointments",
    description: "Honorary appointments (Visiting Lecturer, Honorary Research Fellow) provide access to university infrastructure without full-time commitment.",
    steps: [
      "Approach a physiotherapy or allied health department to discuss an honorary appointment",
      "Contribute to teaching, supervision, or research as part of the role",
      "Useful for maintaining an academic profile while in full-time NHS practice",
      "Often linked to MSc supervision, module delivery, or collaborative research projects",
    ],
  },
];

const FUNDING_SOURCES = [
  { funder: "NIHR", scope: "Health and care research — clinical academic fellowships, doctoral and post-doctoral awards, research grants" },
  { funder: "Versus Arthritis", scope: "Rheumatology and MSK research — PhD studentships, career development fellowships, project grants" },
  { funder: "Medical Research Council", scope: "Basic and clinical science — collaborative grants, CASE studentships with industry" },
  { funder: "CSP Research Foundation", scope: "Physiotherapy-specific research funding — small grants, PhD bursaries, conference travel awards" },
  { funder: "Arthritis Research UK / Kennedy Trust", scope: "Postdoctoral and clinical academic fellowships in musculoskeletal research" },
];

export default function AcademicPhysiotherapy() {
  return (
    <PhysioLayout title="Academic Physiotherapy Career">
      <div className="space-y-6">
        <div className="bg-gradient-to-br from-amber-700 to-amber-900 rounded-2xl p-6 text-white">
          <div className="flex items-center gap-3 mb-3">
            <GraduationCap className="h-8 w-8 text-amber-300" />
            <div>
              <h2 className="text-xl font-bold">Academic Physiotherapy Career</h2>
              <p className="text-amber-300 text-sm">PhD · NIHR Fellowships · Clinical Academic Appointments</p>
            </div>
          </div>
          <p className="text-amber-200 text-sm leading-relaxed">
            Clinical academic physiotherapists combine NHS clinical practice with university-based research and education. A growing number of rheumatology physiotherapists are building successful clinical academic careers through NIHR and research charity funding.
          </p>
        </div>

        <div className="space-y-4">
          {ACADEMIC_ROUTES.map((route) => (
            <div key={route.title} className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
              <h3 className="font-bold text-slate-900 mb-1">{route.title}</h3>
              <p className="text-sm text-slate-500 mb-3 leading-relaxed">{route.description}</p>
              <ul className="space-y-2">
                {route.steps.map((step) => (
                  <li key={step} className="flex items-start gap-2 text-sm text-slate-700">
                    <CheckCircle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
                    {step}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
          <h3 className="font-bold text-slate-900 mb-4">Research Funding Sources</h3>
          <div className="space-y-3">
            {FUNDING_SOURCES.map((f) => (
              <div key={f.funder} className="flex items-start gap-3">
                <span className="text-xs font-bold bg-amber-100 text-amber-700 px-2 py-1 rounded-full shrink-0 mt-0.5">{f.funder}</span>
                <p className="text-sm text-slate-600 leading-relaxed">{f.scope}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5">
          <h3 className="font-bold text-slate-900 mb-2">Tips for Getting Started in Research</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {[
              "Join a trust research and innovation team or clinical trial",
              "Undertake a clinical audit and write it up for presentation",
              "Attend a research methods short course (e.g. NIHR Research Design Service)",
              "Build a relationship with a local university physiotherapy department",
              "Subscribe to Physiotherapy, Annals of Rheumatic Diseases, and JOSPT",
              "Present at ACPR study day, CSP Congress, or BSR Annual Meeting",
              "Pursue an MSc with a research dissertation as a stepping stone to PhD",
              "Network with clinical academic physiotherapists via CSP and ACPR",
            ].map((tip) => (
              <div key={tip} className="flex items-start gap-2 text-sm text-slate-700">
                <div className="w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0 mt-2" />
                {tip}
              </div>
            ))}
          </div>
        </div>
      </div>
    </PhysioLayout>
  );
}
